// Pages that load when they're first opened, as TUNE's analytics site loads its heavy ones (TUNEMFD
// web/src/pages/lazyPages.ts): the Analyser brings its charts and map with it.
import { lazy, type ComponentType } from 'react';

const RELOADED_AT = 'tune-chunk-reload-at';
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * A deploy replaces the hashed files, so a tab opened before it can fail to load a page it hasn't opened yet. It
 * reloads once to take the new build; a second failure soon after is shown.
 */
function recoverFromChunkError(error: unknown): Promise<never> {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT) ?? 0);
    if (Date.now() - last > RELOAD_COOLDOWN_MS) {
      sessionStorage.setItem(RELOADED_AT, String(Date.now()));
      window.location.reload();
      // The fallback stays up until the reload takes over.
      return new Promise<never>(() => {});
    }
  } catch {
    // No storage: the error is shown.
  }
  return Promise.reject(error);
}

export function lazyPage<M, P extends object = object>(load: () => Promise<M>, pick: (module: M) => ComponentType<P>) {
  return lazy(() =>
    load()
      .then((module) => ({ default: pick(module) }))
      .catch(recoverFromChunkError),
  );
}

/** A chunk a route loads before it shows (its `lazy`), recovering from a deploy the same way. */
export function chunk<M>(load: () => Promise<M>): Promise<M> {
  return load().catch(recoverFromChunkError);
}
